import { useCallback, useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, fmtAUD, fmtCompact, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Wallet, Users, Megaphone, Target, LogOut,
  ArrowRight, Sun, Moon,
} from "lucide-react";
import { VisionSlideshow, useVisionImages } from "@/components/VisionSlideshow";
import { VisionBoard } from "@/components/VisionBoard";
import { PurposeRow } from "@/components/PurposeRow";
import { DesiresRow } from "@/components/DesiresRow";
import { OperatingSystem } from "@/components/OperatingSystem";
import { MorningBriefing } from "@/components/MorningBriefing";
import { TravelWidget } from "@/components/TravelWidget";
import { FocusBoard } from "@/components/FocusBoard";
import { toggleTheme } from "@/lib/theme";

const HERO_IMG = "https://images.unsplash.com/photo-1762716514222-996318bb41a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdsb3dpbmclMjBuZW9uJTIwc3BoZXJlfGVufDB8fHx8MTc4NTg4ODQyNHww&ixlib=rb-4.1.0&q=85";

const pctDelta = (cur, prev) => {
  if (!prev) return 0;
  return Math.round(((cur - prev) / prev) * 100);
};

const Delta = ({ value, testid }) => {
  const up = value >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-emerald-400" : "text-red-400"}`} data-testid={testid}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up ? "+" : ""}{value}%
    </span>
  );
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [focusTasks, setFocusTasks] = useState([]);
  const { images: visionImages, reload: reloadVision } = useVisionImages();
  const [theme, setTheme] = useState(localStorage.getItem("lbd-theme") || "dark");

  const load = useCallback(async () => {
    const [dash, tasks] = await Promise.all([api.get("/dashboard"), api.get("/tasks/open")]);
    setData(dash.data);
    setFocusTasks(tasks.data.tasks);
  }, []);

  useEffect(() => { load().catch((err) => { console.error("Dashboard load failed:", err); toast.error("Failed to load dashboard"); }); }, [load]);

  const toggleTask = async (id) => {
    try {
      await api.patch(`/tasks/${id}/toggle`);
      setFocusTasks((prev) => prev.filter((t) => t.id !== id));
      toast.success("Small win logged. Keep going.");
      load().catch((err) => console.error("Dashboard reload failed:", err));
    } catch { toast.error("Couldn't update task"); }
  };

  const totals = useMemo(() => {
    if (!data) return null;
    const live = data.entities.filter((e) => e.type !== "personal");
    return {
      bank: live.reduce((s, e) => s + (e.bank_balance || 0), 0),
      visitors: live.reduce((s, e) => s + (e.latest?.visitors || 0), 0),
      spend: live.reduce((s, e) => s + (e.latest?.ad_spend || 0), 0),
      reach: live.reduce((s, e) => s + (e.latest?.reach || 0), 0),
      conversions: live.reduce((s, e) => s + (e.latest?.conversions || 0), 0),
    };
  }, [data]);

  if (!data || !totals) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="dashboard-loading">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100" data-testid="dashboard-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-2xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">Holding Company</p>
            <h1 className="font-display text-xl text-white" data-testid="dashboard-brand">LIFE BY DESIGN</h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setTheme(toggleTheme())}
              className="text-zinc-400 hover:text-[#D4AF37] transition-colors duration-200"
              data-testid="theme-toggle"
              aria-label="Toggle light/dark theme"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <span className="text-xs text-zinc-500 hidden sm:block" data-testid="dashboard-user">{user?.email}</span>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-[#D4AF37] transition-colors duration-200"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" /> Exit
            </button>
          </div>
        </div>
      </header>

      {/* My Purpose */}
      <MorningBriefing tasks={focusTasks} />
      <PurposeRow />
      <DesiresRow />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <VisionSlideshow images={visionImages} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-black/15" />
        <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute inset-0 gold-glow" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 sm:py-24">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="text-xs tracking-[0.5em] uppercase text-[#00E5FF] mb-4" data-testid="hero-eyebrow">
            The Empire at a Glance
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl leading-tight" data-testid="hero-title">
            Small wins today.<br />Empires tomorrow.
          </motion.h2>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10 mt-12 rounded-sm overflow-hidden"
            data-testid="totals-strip"
          >
            {[
              { label: "Total Bank", value: fmtAUD(totals.bank), icon: Wallet, id: "total-bank" },
              { label: "Visitors / wk", value: fmtCompact(totals.visitors), icon: Users, id: "total-visitors" },
              { label: "Ad Spend / wk", value: fmtAUD(totals.spend), icon: Megaphone, id: "total-spend" },
              { label: "Reach / wk", value: fmtCompact(totals.reach), icon: TrendingUp, id: "total-reach" },
              { label: "Conversions / wk", value: fmtNum(totals.conversions), icon: Target, id: "total-conversions" },
            ].map((s, i) => (
              <div key={s.id} className={`bg-[#0F0F11] p-5 ${i === 0 ? "col-span-2 md:col-span-1" : ""}`} data-testid={s.id}>
                <s.icon className="w-4 h-4 text-[#D4AF37] mb-3" />
                <p className="text-lg sm:text-xl font-bold text-white">{s.value}</p>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Entity bento grid */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <h3 className="font-display text-2xl sm:text-3xl text-white" data-testid="entities-heading">The Portfolio</h3>
          <p className="text-xs uppercase tracking-widest text-zinc-500">Click a venture to go deeper</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.entities.filter((e) => e.type !== "personal").map((e, i) => (
            <motion.div key={e.slug} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * i }}>
              <Link
                to={`/business/${e.slug}`}
                className="group relative block h-80 rounded-md overflow-hidden border border-white/10 hover:border-[#D4AF37]/60 transition-colors duration-300 hover:-translate-y-1 will-change-transform"
                data-testid={`entity-card-${e.slug}`}
              >
                <img src={e.image} alt={e.name} className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-60 transition-opacity duration-500" />
                <div className="absolute inset-0 bg-black/60" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                {e.status === "pre-launch" && (
                  <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-[0.25em] bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/40 px-3 py-1 rounded-full" data-testid={`badge-${e.slug}`}>
                    Pre-Launch
                  </span>
                )}
                <div className="relative h-full flex flex-col justify-end p-6">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-1">{e.tagline}</p>
                  <h4 className="font-display text-2xl text-white mb-4">{e.name}</h4>
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div>
                      <p className="text-zinc-500 uppercase tracking-wider text-[9px]">Bank</p>
                      <p className="font-bold text-white">{fmtAUD(e.bank_balance)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 uppercase tracking-wider text-[9px]">Visitors</p>
                      <p className="font-bold text-white">{fmtCompact(e.latest?.visitors)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 uppercase tracking-wider text-[9px]">Conv.</p>
                      <p className="font-bold text-white">{fmtNum(e.latest?.conversions)}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                    <Delta value={pctDelta(e.latest?.visitors || 0, e.prev?.visitors || 0)} testid={`delta-${e.slug}`} />
                    <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-300 inline-flex items-center gap-1">
                      Open <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Travel & experiences */}
      <TravelWidget />

      {/* Focus tasks — one column per venture */}
      <FocusBoard tasks={focusTasks} onToggle={toggleTask} onAdded={load} />

      {/* Vision board */}
      <VisionBoard images={visionImages} reload={reloadVision} />

      {/* My operating system */}
      <OperatingSystem />
    </div>
  );
}
